import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyExpenseAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './PropertyExpenseCreate.css';

const PropertyExpenseCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    expenseDescription: '',
    amount: '',
    invoiceNumber: '',
    payingEntity: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) {
      newErrors.date = t('propertyExpense.create.errors.dateRequired');
    }
    if (!formData.expenseDescription.trim()) {
      newErrors.expenseDescription = t('propertyExpense.create.errors.expenseDescriptionRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('propertyExpense.create.errors.amountRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      await propertyExpenseAPI.create(submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/property-expense');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('propertyExpense.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('propertyExpense.create.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.date ? 'is-invalid' : ''}`}
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
              {errors.date && (
                <div className="error-message">{errors.date}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('propertyExpense.create.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.create.placeholders.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-alt"></i> {t('propertyExpense.create.fields.expenseDescription')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-alt input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.expenseDescription ? 'is-invalid' : ''}`}
                  name="expenseDescription"
                  value={formData.expenseDescription}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.create.placeholders.expenseDescription')}
                  maxLength={500}
                />
              </div>
              {errors.expenseDescription && (
                <div className="error-message">{errors.expenseDescription}</div>
              )}
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-receipt"></i> {t('propertyExpense.create.fields.invoiceNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-receipt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.create.placeholders.invoiceNumber')}
                  maxLength={100}
                />
              </div>
              </div>
            </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('propertyExpense.create.fields.payingEntity')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="payingEntity"
                  value={formData.payingEntity}
                  onChange={handleChange}
                  placeholder={t('propertyExpense.create.placeholders.payingEntity')}
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/property-expense')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('propertyExpense.create.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('propertyExpense.create.messages.createSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default PropertyExpenseCreate;


